import React, { useState } from 'react';
import { InfluencerProfile, TabId } from './InfluencerProfile';
import { motion, AnimatePresence } from 'framer-motion';
import { Building2, UserCircle, BarChart3, DollarSign, ChevronRight, IdCard, FileText } from 'lucide-react';
type MenuItem = {
  id: string;
  tab: TabId;
  section?: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  badge?: string;
};
export const InteractiveProfile = () => {
  const [activeTab, setActiveTab] = useState<TabId>('social');
  // Which region of the Social Media tab is spotlighted.
  const [activeSection, setActiveSection] = useState<string | null>('agency');
  // Which left-menu item is expanded.
  const [openItem, setOpenItem] = useState<string>('agency');
  const items: MenuItem[] = [{
    id: 'agency',
    tab: 'social',
    section: 'agency',
    icon: <Building2 size={18} data-id="element-749" data-mp="fk3edzyhssqc" />,
    title: 'Agency details',
    description: "The talent agency manages this creator's profile — they input the specific talent manager's name, direct email, and phone number, and set whether the talent is exclusive or non-exclusive. This is how you reach out."
  }, {
    id: 'influencer',
    tab: 'social',
    section: 'influencer',
    icon: <UserCircle size={18} data-id="element-750" data-mp="9rp7nbtsmtfj" />,
    title: 'Influencer data',
    description: 'Defined by the talent agency — the creator\u2019s name, social links, collaboration types, location, niche, and content-style tags. The photo is auto-pulled from their social media.'
  }, {
    id: 'analytics',
    tab: 'social',
    section: 'analytics',
    icon: <BarChart3 size={18} data-id="element-751" data-mp="3n1d3yj0lzzqe" />,
    title: 'Live analytics',
    badge: 'Live · from Zano',
    description: 'The only part Zano provides — real-time data across TikTok, Instagram, and YouTube: followers, average views, average likes, engagement rate, and audience demographics. For Instagram, we also surface city-level audience data.'
  }, {
    id: 'pricing',
    tab: 'social',
    section: 'pricing',
    icon: <DollarSign size={18} data-id="element-752" data-mp="1zce80190if6t" />,
    title: 'Pricing',
    description: 'Defined by the talent agency — the rates and deliverables they set directly, whether price ranges or flat fees, plus their notes on revisions and lead times.'
  }, {
    id: 'personal',
    tab: 'personal',
    icon: <IdCard size={18} data-id="element-753" data-mp="1vaj5wuj9bxrz" />,
    title: 'Personal information',
    description: 'Defined by the talent agency — a tab they maintain with more personal details like real name, languages, gender, age, and location (country, state, and city).'
  }, {
    id: 'notes',
    tab: 'notes',
    icon: <FileText size={18} data-id="element-754" data-mp="2e3engq86f9cg" />,
    title: 'Notes',
    description: 'Defined by the talent agency — an open bio the talent manager writes covering background, past collaborations, awards, and a general description. The keywords here power search when brands and agencies query the database.'
  }];
  const handleSelect = (item: MenuItem) => {
    setOpenItem(item.id);
    setActiveTab(item.tab);
    // Only the Social Media tab has spotlightable regions.
    setActiveSection(item.tab === 'social' ? item.section ?? null : null);
  };
  return <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start w-full" data-id="element-755" data-mp="144vhvki5gixh">
      {/* Left side: Explainer Menu */}
      <div className="w-full lg:w-1/3 flex flex-col gap-3 shrink-0" data-id="element-756" data-mp="z4hhmomsida0">
        <div className="mb-4" data-id="element-757" data-mp="bh9cc6pd347z">
          <h3 className="text-2xl font-bold text-zano-charcoal mb-2" data-id="element-758" data-mp="beddmg5owbih">
            The anatomy of a profile
          </h3>
          <p className="text-sm text-gray-600" data-id="element-759" data-mp="l69gs5mmurtk">
            Click a section to see how Zano brings live social data together
            with direct agency access.
          </p>
        </div>

        <div className="flex flex-col gap-2" data-id="element-760" data-mp="3ovcqd2sw3sdt">
          {items.map(item => {
          const isOpen = openItem === item.id;
          return <div key={item.id} className={`rounded-xl border transition-all duration-300 overflow-hidden cursor-pointer ${isOpen ? 'border-zano-pink bg-white shadow-md' : 'border-gray-200 bg-gray-50/50 hover:border-zano-pink/40 hover:bg-white'}`} onClick={() => handleSelect(item)} data-id="element-761" data-mp="3jw034gy3wjap">
                <div className="p-4 flex items-center justify-between" data-id="element-762" data-mp="g2idjpx33hwf">
                  <div className="flex items-center gap-3" data-id="element-763" data-mp="g4uzsf9vkefa">
                    <div className={`p-2 rounded-lg ${isOpen ? 'bg-zano-blush text-zano-pink' : 'bg-white text-gray-500 shadow-sm'}`} data-id="element-764" data-mp="k3kcs6r03tye">
                      {item.icon}
                    </div>
                    <div className="flex items-center gap-2" data-id="element-765" data-mp="u0p22fg585jf">
                      <h4 className={`font-semibold ${isOpen ? 'text-zano-charcoal' : 'text-gray-700'}`} data-id="element-766" data-mp="z0bgutbmdtuc">
                        {item.title}
                      </h4>
                      {item.badge && <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-[10px] font-semibold" data-id="element-767" data-mp="1hxmr1ld13m95">
                          {item.badge}
                        </span>}
                    </div>
                  </div>
                  <ChevronRight size={16} className={`transition-transform duration-300 ${isOpen ? 'rotate-90 text-zano-pink' : 'text-gray-400'}`} data-id="element-768" data-mp="146qc2xc0z3kk" />
                </div>

                <AnimatePresence data-id="element-769" data-mp="1ww71rn89g6d3">
                  {isOpen && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.2
              }} data-id="element-770" data-mp="1rvsrdyj6skbw">
                      <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed border-t border-gray-100 mt-2 pt-3" data-id="element-771" data-mp="hxj7w5b33gxc">
                        {item.description}
                      </div>
                    </motion.div>}
                </AnimatePresence>
              </div>;
        })}
        </div>
      </div>

      {/* Right side: Interactive Profile */}
      <div className="w-full lg:w-2/3 relative" data-id="element-772" data-mp="3v5cu6t862fiw">
        {/* Decorative background glow */}
        <div className="absolute -inset-10 bg-gradient-to-tr from-zano-pink/10 via-zano-lavender/20 to-sky-100/20 rounded-[3rem] blur-3xl -z-10" data-id="element-773" data-mp="3he7ujr4stn8k" />

        <div className="relative w-full max-w-2xl mx-auto" data-id="element-774" data-mp="3lcf6m8obi0df">
          <InfluencerProfile activeSection={activeSection} activeTab={activeTab} onTabChange={tab => {
          setActiveTab(tab);
          // Sync the left menu when the user clicks tabs on the card itself.
          const match = items.find(i => i.tab === tab);
          if (tab !== 'social') {
            setActiveSection(null);
            if (match) setOpenItem(match.id);
          } else {
            setActiveSection('agency');
            setOpenItem('agency');
          }
        }} onSectionClick={section => {
          // When user clicks a section on the profile, sync the left menu
          const match = items.find(i => i.section === section);
          if (match) {
            handleSelect(match);
          }
        }} data-id="element-775" data-mp="37gbkz3rif0ck" />
        </div>
      </div>
    </div>;
};